import { useState, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { supabase } from "@lib/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@shared/components/ui/card";
import { Button } from "@shared/components/ui/button";
import { Input } from "@shared/components/ui/input";
import { Textarea } from "@shared/components/ui/textarea";
import { Label } from "@shared/components/ui/label";
import { Star, Quote, Send, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";

interface ClinicTestimonialsSectionProps {
  clinicId: string;
  specialistId: string;
  clinicName: string;
  primaryColor: string;
  isArabic: boolean;
  pageConfig: Record<string, unknown>;
}

interface ClinicTestimonial {
  id: string;
  client_name: string;
  rating: number;
  review_text: string;
  created_at: string;
}

const reviewSchema = z.object({
  client_name: z.string().trim().min(2, "الاسم مطلوب (حرفين على الأقل)").max(100),
  client_email: z.string().trim().email("البريد الإلكتروني غير صحيح").max(255).optional().or(z.literal("")),
  client_phone: z.string().trim().max(20).optional().or(z.literal("")),
  rating: z.number().min(1).max(5),
  review_text: z.string().trim().min(10, "التقييم مطلوب (10 أحرف على الأقل)").max(1000),
});

const ClinicTestimonialsSection = ({ clinicId, specialistId, clinicName, primaryColor, isArabic }: ClinicTestimonialsSectionProps) => {
  const [testimonials, setTestimonials] = useState<ClinicTestimonial[]>([]);
  const [loading, setLoading] = useState(true);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [formData, setFormData] = useState({
    client_name: "",
    client_email: "",
    client_phone: "",
    rating: 5,
    review_text: "",
  });

  useEffect(() => {
    fetchTestimonials();
  }, [clinicId]);

  const fetchTestimonials = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("clinic_testimonials")
      .select("id, client_name, rating, review_text, created_at")
      .eq("clinic_id", clinicId)
      .eq("is_approved", true)
      .order("created_at", { ascending: false });

    if (!error) {
      setTestimonials(data || []);
    }
    setLoading(false);
  };

  const submitMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const result = reviewSchema.safeParse(data);
      if (!result.success) {
        const errors: Record<string, string> = {};
        result.error.errors.forEach((err) => {
          if (err.path[0]) errors[err.path[0] as string] = err.message;
        });
        throw { validationErrors: errors };
      }

      const { error } = await supabase
        .from("clinic_testimonials")
        .insert({
          clinic_id: clinicId,
          client_name: data.client_name.trim(),
          client_email: data.client_email?.trim() || null,
          client_phone: data.client_phone?.trim() || null,
          rating: data.rating,
          review_text: data.review_text.trim(),
        });

      if (error) throw error;

      // Notify specialist of new review (fire and forget)
      supabase.functions.invoke('send-clinic-notification', {
        body: {
          type: 'specialist_new_review',
          specialistId,
          clientEmail: '',
          clientName: data.client_name.trim(),
          clinicName,
          rating: data.rating,
          reviewText: data.review_text.trim(),
        },
      }).catch((e) => console.error('Review notification failed:', e));
    },
    onSuccess: () => {
      setFormSubmitted(true);
      toast.success(isArabic ? "تم إرسال تقييمك بنجاح" : "Your review has been submitted successfully");
    },
    onError: (error: unknown) => {
      const e = error as { validationErrors?: Record<string, string> };
      if (e.validationErrors) {
        setFormErrors(e.validationErrors);
      } else {
        toast.error(isArabic ? "حدث خطأ أثناء إرسال التقييم" : "Error submitting review");
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormErrors({});
    submitMutation.mutate(formData);
  };

  const renderStars = (rating: number, interactive = false) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-5 w-5 ${interactive ? "cursor-pointer" : ""} ${
              star <= rating ? "fill-yellow-400 text-yellow-400" : "text-gray-300"
            }`}
            onClick={interactive ? () => setFormData({ ...formData, rating: star }) : undefined}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-8">
      {/* Submission Form */}
      <Card>
        <CardHeader>
          <CardTitle>{isArabic ? "شارك رأيك" : "Share Your Review"}</CardTitle>
          <CardDescription>
            {isArabic ? "نقدر رأيك وتجربتك مع خدماتنا" : "We value your feedback and experience with our services"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {formSubmitted ? (
            <div className="text-center py-8">
              <div
                className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
                style={{ backgroundColor: `${primaryColor}15` }}
              >
                <CheckCircle className="h-8 w-8" style={{ color: primaryColor }} />
              </div>
              <h3 className="text-xl font-bold mb-2">
                {isArabic ? "تم إرسال تقييمك بنجاح!" : "Your review has been submitted!"}
              </h3>
              <p className="text-muted-foreground mb-4">
                {isArabic ? "سيتم مراجعته ونشره قريباً" : "It will be reviewed and published soon"}
              </p>
              <Button
                variant="outline"
                onClick={() => {
                  setFormSubmitted(false);
                  setFormData({ client_name: "", client_email: "", client_phone: "", rating: 5, review_text: "" });
                }}
              >
                {isArabic ? "إرسال تقييم آخر" : "Submit Another Review"}
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{isArabic ? "الاسم *" : "Name *"}</Label>
                  <Input
                    value={formData.client_name}
                    onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                    placeholder={isArabic ? "أدخل اسمك" : "Enter your name"}
                    className={formErrors.client_name ? "border-red-500" : ""}
                    maxLength={100}
                  />
                  {formErrors.client_name && (
                    <p className="text-sm text-red-500">{formErrors.client_name}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>{isArabic ? "البريد الإلكتروني (اختياري)" : "Email (optional)"}</Label>
                  <Input
                    type="email"
                    value={formData.client_email}
                    onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                    placeholder="example@email.com"
                    dir="ltr"
                    className={formErrors.client_email ? "border-red-500" : ""}
                    maxLength={255}
                  />
                  {formErrors.client_email && (
                    <p className="text-sm text-red-500">{formErrors.client_email}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>{isArabic ? "رقم الجوال (اختياري)" : "Phone (optional)"}</Label>
                  <Input
                    type="tel"
                    value={formData.client_phone}
                    onChange={(e) => setFormData({ ...formData, client_phone: e.target.value })}
                    placeholder="05xxxxxxxx"
                    dir="ltr"
                    maxLength={20}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>{isArabic ? "التقييم *" : "Rating *"}</Label>
                {renderStars(formData.rating, true)}
              </div>

              <div className="space-y-2">
                <Label>{isArabic ? "رأيك *" : "Your Review *"}</Label>
                <Textarea
                  value={formData.review_text}
                  onChange={(e) => setFormData({ ...formData, review_text: e.target.value })}
                  placeholder={isArabic ? "شارك تجربتك مع خدماتنا..." : "Share your experience with our services..."}
                  rows={4}
                  className={formErrors.review_text ? "border-red-500" : ""}
                  maxLength={1000}
                />
                {formErrors.review_text && (
                  <p className="text-sm text-red-500">{formErrors.review_text}</p>
                )}
                <p className="text-xs text-muted-foreground text-left" dir="ltr">
                  {formData.review_text.length} / 1000
                </p>
              </div>

              <Button
                type="submit"
                className="w-full gap-2 text-white"
                style={{ backgroundColor: primaryColor }}
                disabled={submitMutation.isPending}
              >
                <Send className="h-4 w-4" />
                {submitMutation.isPending
                  ? isArabic ? "جاري الإرسال..." : "Sending..."
                  : isArabic ? "إرسال التقييم" : "Submit Review"}
              </Button>

              <p className="text-xs text-muted-foreground text-center">
                {isArabic ? "سيتم مراجعة تقييمك قبل نشره" : "Your review will be reviewed before publishing"}
              </p>
            </form>
          )}
        </CardContent>
      </Card>

      {/* Display Approved Testimonials */}
      {loading ? (
        <div className="text-center py-8">
          <p className="text-muted-foreground">{isArabic ? "جاري التحميل..." : "Loading..."}</p>
        </div>
      ) : testimonials.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-muted-foreground">
            {isArabic ? "لا توجد تقييمات منشورة حالياً. كن أول من يشارك رأيه!" : "No reviews yet. Be the first to share your feedback!"}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {testimonials.map((testimonial) => (
            <Card key={testimonial.id} className="hover:shadow-md transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
                      style={{ backgroundColor: primaryColor }}
                    >
                      {testimonial.client_name.charAt(0)}
                    </div>
                    <div>
                      <h4 className="font-semibold">{testimonial.client_name}</h4>
                      <p className="text-xs text-muted-foreground">
                        {new Date(testimonial.created_at).toLocaleDateString("ar-SA")}
                      </p>
                    </div>
                  </div>
                  <Quote className="h-6 w-6 text-muted-foreground/20" />
                </div>
                <div className="mb-3">{renderStars(testimonial.rating)}</div>
                <p className="text-sm text-muted-foreground leading-relaxed">{testimonial.review_text}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default ClinicTestimonialsSection;
